import React, { Component } from "react";
import {
  FilterButton,
  FilterContent,
  DateRangeWrapper,
  DateRangeIconWrapper,
  SelectWrapper,
  FilterActionWrapper,
  ClearButton,
} from "./styledComponents";
import Icon from "components/Icon";
import Popover from "components/Popover";
import { colors } from "globalStyles";
import InputField, { Select, Date } from "components/InputField";
import Button from "components/Button";
import {
  selectIsFetching,
  selectAllChannels,
} from "../../ChannelsPage/selectors";
import {
  fetchChannelDetailRequest,
  fetchChannelsRequest,
} from "../../ChannelsPage/actions";
import { createStructuredSelector } from "reselect";
import { mapIsEmpty } from "utils/helpers";
import { connect } from "react-redux";
import { withUseFormHook } from "utils/hooks";
import { Controller } from "react-hook-form";
import { t } from "copy/t";

const FilterInput = ({ label, type, children, ...props }) => {
  const fieldName = props.name || (props.field && props.field.name) || "date";
  const controlId =
    type === "date_range" ? `${fieldName}_start_date` : fieldName;
  var content = children;
  switch (type) {
    case "date_range":
      content = (
        <DateRangeWrapper>
          <DateRangeIconWrapper>
            <Icon name="calendar" size="14" viewBox="0 0 14 14" />
            <Icon name="calendar" />
          </DateRangeIconWrapper>
          <Date
            isDateRangeMode
            {...props}
            customArrowIcon={<Icon name="-" size="20" />}
          />
        </DateRangeWrapper>
      );
      break;
    case "select":
      content = (
        <SelectWrapper>
          <Select
            {...props}
            id={controlId}
            placeholder={t("placeholders.allMyChannels")}
          />
        </SelectWrapper>
      );
      break;
  }
  return (
    <div>
      <label htmlFor={controlId}>{label}</label>
      {content}
    </div>
  );
};

// Fetch channel options when the popover panel mounts. The previous
// fetch lived on an inner div's onClick, so keyboard activation of the
// Filter <button> opened an empty source-channel list.
const FetchChannelsOnOpen = ({ fetchChannels, shouldFetch }) => {
  React.useEffect(() => {
    if (shouldFetch) fetchChannels();
  }, [fetchChannels, shouldFetch]);
  return null;
};

export class Filter extends Component {
  state = {
    channelOptions: []
  };

  componentDidMount() {
    const {
      params: { channelId },
    } = this.props;
    if (channelId) {
      this.props.fetchDetail(channelId);
    }
  }

  componentDidUpdate(prevProps) {
    const newChannels = this.getChannels(this.props.channels);
    if (
      JSON.stringify(this.getChannels(prevProps.channels)) !==
        JSON.stringify(newChannels) ||
      (this.state.channelOptions.length === 0 &&
        !mapIsEmpty(prevProps.channels))
    ) {
      this.setState({
        channelOptions:
          this.props.channelPaging.currentPage === 1
            ? newChannels
            : [...this.state.channelOptions, ...newChannels],
      });
    }
    const { formData, filterSubmit } = this.props;
    const oldFormData = prevProps.formData;
    const newFilter = formData && (formData.dateRange || formData.sourceChannel || formData.search);
    const oldFilter = oldFormData && (oldFormData.dateRange || oldFormData.sourceChannel || oldFormData.search);
    if(oldFilter && !newFilter) {
      filterSubmit();
    }

    if (
      (!prevProps.channelDetail && this.props.channelDetail) &&
      JSON.stringify(prevProps.channelDetail) !==
        JSON.stringify(this.props.channelDetail)
    ) {
      this.setInitChannelSource(this.props.channelDetail.id);
      this.setState({
        channelOptions: [
          ...this.state.channelOptions,
          {
            label: this.props.channelDetail.name,
            name: this.props.channelDetail.id,
          },
        ],
      });
    }
  }

  getChannels(channels) {
    const channelsEmpty = !channels || mapIsEmpty(channels);
    const channelsSource = channelsEmpty
      ? []
      : Object.keys(channels)
          .map((k) => ({ label: channels[k].name, value: channels[k].id }))
          .filter((x) => x.label);
    return channelsSource;
  }

  setInitChannelSource(data) {
    const form = this.props.form;
    form.setValue("source_channel", data);
  }

  render() {
    const {
      filterSubmit,
      isFetching,
      fetchChannels,
      formData,
      resetForm,
      channelPaging,
      form,
    } = this.props;
    const { control, getValues, setValue, watch } = form;

    const hasFilter =
      formData &&
      (formData.dateRange || formData.sourceChannel || formData.search);
    return (
      <Popover>
        <FilterButton>
          <span>Filter</span>
          <Icon name="drop-down" size="12" fill={colors.DARK} />
        </FilterButton>
        {(toggleOpen) => (
          <FilterContent>
            <FetchChannelsOnOpen
              fetchChannels={fetchChannels}
              shouldFetch={
                !isFetching && this.state.channelOptions.length === 0
              }
            />
            <div>
              <form>
                <Controller
                  control={control}
                  name="date_range"
                  render={({ field: { ref, ...fieldRest } }) => (
                    <FilterInput
                      name="date_range"
                      type="date_range"
                      label="date range"
                      field={fieldRest}
                      {...fieldRest}
                    />
                  )}
                />
                <Controller
                  control={control}
                  name="source_channel"
                  render={({ field: { ref, ...fieldRest } }) => (
                    <FilterInput
                      name="source_channel"
                      type="select"
                      label="source channel"
                      fetchOptions={fetchChannels}
                      pagingOptions={channelPaging}
                      alwaysFetchOptions={true}
                      options={this.state.channelOptions}
                      isFetching={isFetching}
                      enableDropDownIcon={true}
                      field={fieldRest}
                      {...fieldRest}
                    />
                  )}
                />
               
                <FilterActionWrapper>
                  <ClearButton
                    type="button"
                    onClick={() => {
                      if (hasFilter) {
                        form.reset();
                      }
                      toggleOpen();
                    }}
                  >
                    {hasFilter ? "Clear" : "Cancel"}
                  </ClearButton>
                  <Button
                    type="button"
                    onClick={() => {
                      toggleOpen();
                      filterSubmit();
                    }}
                  >
                    Apply
                  </Button>
                </FilterActionWrapper>
              </form>
            </div>
          </FilterContent>
        )}
      </Popover>
    );
  }
}

const mapStateToProps = createStructuredSelector({
  isFetching: selectIsFetching(),
  channels: selectAllChannels(),
  channelPaging: (state) => state.channels.paging,
  channelDetail: (state) => state.channels.channelDetail,
});

const mapDispatchToProp = (dispatch) => ({
  fetchChannels: (params) => dispatch(fetchChannelsRequest(params)),
  fetchDetail: (id) => dispatch(fetchChannelDetailRequest(id)),
});

export default connect(
  mapStateToProps,
  mapDispatchToProp
)(Filter);
